import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mapModifiers } from 'src/libs/component';
import { useAppSelector } from 'src/redux/hooks';
import { SimpleCaptcha } from './captcha';
import { toast } from 'react-toastify';
import { getDeviceId } from 'src/api/feedback';
import { useGetFeedbackHistory } from 'src/api/hooks/feedbacks/useGetFeedbackHistory';
import { useSubmitFeedback } from 'src/api/hooks/feedbacks/useSubmitFeedback';

// ────────────────────────────────────────
// Types
// ────────────────────────────────────────
export interface FeedbackData {
  content: string;
  contact?: string;
  images: File[];
  deviceId: string;
  userId?: number;
}

export interface FeedbackProps {
  /** 'page' = full page | 'popup' = floating icon */
  mode?: 'page' | 'popup';
  /** Position of the floating icon (popup mode only) */
  popupPosition?: { bottom?: number; right?: number; left?: number; top?: number };
  /** Custom handler – receives sanitised payload. If omitted, uses built-in API. */
  onSubmit?: (data: FeedbackData) => Promise<void> | void;
  /** Maximum images allowed (default 3) */
  maxImages?: number;
  /** Maximum file size in MB (default 3) */
  maxFileSizeMB?: number;
}

const MAX_IMAGES_DEFAULT = 3;
const MAX_FILE_SIZE_MB_DEFAULT = 3;

// ── Contact field regex constants ────────
const PHONE_REGEX = /^\+?[0-9]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Status badge helper ──────────────────
const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const { t } = useTranslation();
  const labelMap: Record<string, string> = {
    pending: 'feedback.status_pending',
    read: 'feedback.status_read',
    replied: 'feedback.status_replied',
  };
  const key = status || 'pending';
  return (
    <span className={`o-feedback__history-status o-feedback__history-status--${key}`}>
      {t(labelMap[key] || labelMap.pending)}
    </span>
  );
};

// ────────────────────────────────────────
// Component
// ────────────────────────────────────────
export const Feedback: React.FC<FeedbackProps> = ({
  mode = 'popup',
  popupPosition = { bottom: 24, right: 24 },
  onSubmit,
  maxImages = MAX_IMAGES_DEFAULT,
  maxFileSizeMB = MAX_FILE_SIZE_MB_DEFAULT,
}) => {
  const { t } = useTranslation();
  const userInfo = useAppSelector(state => state.authSlice?.userInfo);
  const isAnonymous = !userInfo?.id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deviceId = useRef(getDeviceId()).current;

  // ── State ──────────────────────────────
  const [isOpen, setIsOpen] = useState(mode === 'page');
  const [view, setView] = useState<'form' | 'history'>('form');
  const [content, setContent] = useState('');
  const [contact, setContact] = useState('');  // email or phone
  const [images, setImages] = useState<{ file: File; preview: string }[]>([]);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [errors, setErrors] = useState<{ content?: string; contact?: string; captcha?: string }>({});
  const [contactHint, setContactHint] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [historyInitialized, setHistoryInitialized] = useState(false);

  // Khi mở chat Coze → đóng modal Góp ý nếu đang mở; giữ icon bên trái chat
  useEffect(() => {
    if (mode !== 'popup') return;

    const handleCozeChatOpen = () => {
      setIsOpen(false);
      setView('form');
      // Đảm bảo icon Góp ý nằm trái chat sau khi remount
      requestAnimationFrame(() => {
        const btn = document.querySelector('.o-feedback__trigger') as HTMLElement | null;
        if (btn && document.documentElement.classList.contains('coze-chat-open')) {
          btn.style.setProperty('right', '508px', 'important');
        }
      });
    };

    window.addEventListener('coze-chat-open', handleCozeChatOpen);
    return () => window.removeEventListener('coze-chat-open', handleCozeChatOpen);
  }, [mode]);

  // ── React-query hooks ──────────────────
  const {
    historyItems,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useGetFeedbackHistory(
    { deviceId, userId: userInfo?.id },
    !isAnonymous && view === 'history', // only fetch for logged-in users on history tab
  );

  // ── Auto-expand first replied item when history loads ──
  React.useEffect(() => {
    if (!historyInitialized && !historyLoading && historyItems.length > 0 && view === 'history') {
      const firstReplied = historyItems.find(item => item.status === 'replied');
      if (firstReplied) {
        setExpandedId(firstReplied.id);
      }
      setHistoryInitialized(true);
    }
  }, [historyItems, historyLoading, historyInitialized, view]);

  const { submitFeedback, isSubmitting: submitting } = useSubmitFeedback();



  // ── Validation ─────────────────────────
  const validate = useCallback((): boolean => {
    const newErrors: typeof errors = {};
    if (!content.trim()) {
      newErrors.content = 'feedback.error_content_required';
    }
    if (!captchaVerified) {
      newErrors.captcha = 'feedback.error_captcha';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [content, contact, captchaVerified]);

  // ── Image handling ─────────────────────
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    const remaining = maxImages - images.length;

    if (remaining <= 0) {
      toast.warning(t('feedback.max_images_reached', { max: maxImages }));
      return;
    }

    const newFiles: { file: File; preview: string }[] = [];
    const fileArray = Array.from(files).slice(0, remaining);

    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        toast.warning(t('feedback.only_images'));
        continue;
      }
      if (file.size > maxSizeBytes) {
        toast.warning(t('feedback.file_too_large', { max: maxFileSizeMB }));
        continue;
      }
      newFiles.push({ file, preview: URL.createObjectURL(file) });
    }

    setImages(prev => [...prev, ...newFiles]);
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  // ── Submit ─────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const trimmedContact = contact.trim();
    const payload: FeedbackData = {
      content: content.trim(),
      contact: isAnonymous && trimmedContact ? trimmedContact : undefined,
      images: images.map(i => i.file),
      deviceId,
      userId: userInfo?.id,
    };

    try {
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await submitFeedback({
          payload: {
            content: payload.content,
            contact: payload.contact,
            deviceId: payload.deviceId,
            userId: payload.userId,
          },
          images: payload.images,
        });
      }
      toast.success(t('feedback.success'));
      resetForm();
      refetchHistory();
      if (mode === 'popup') setIsOpen(false);
    } catch {
      toast.error(t('feedback.error_submit'));
    }
  };

  const resetForm = () => {
    setContent('');
    setContact('');
    setContactHint(null);
    images.forEach(i => URL.revokeObjectURL(i.preview));
    setImages([]);
    setCaptchaVerified(false);
    setErrors({});
  };

  // ── Format date helper ─────────────────
  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // ── Render: History view ───────────────
  const renderHistory = () => (
    <div className="o-feedback__history ">
      <div className="o-feedback__history-header">
        <button
          type="button"
          className="o-feedback__history-back"
          onClick={() => setView('form')}
        >
          ← {t('feedback.back_to_form')}
        </button>
        <h3 className="o-feedback__history-title">{t('feedback.history_title')}</h3>
      </div>

      {historyLoading && (
        <div className="o-feedback__history-loading">
          <span className="o-feedback__spinner" />
          {t('feedback.loading')}
        </div>
      )}

      {!historyLoading && historyItems.length === 0 && (
        <p className="o-feedback__history-empty">{t('feedback.history_empty')}</p>
      )}

      <div className="o-feedback__history-list">
        {historyItems.map(item => {
          const isExpanded = expandedId === item.id;
          return (
            <div
              key={item.id}
              className={mapModifiers('o-feedback__history-item', isExpanded && 'expanded', item.status === 'replied' && 'replied', item.status === 'read' && 'read', item.status === 'pending' && 'pending')}
              onClick={() => setExpandedId(isExpanded ? null : item.id)}
            >
              <div className="o-feedback__history-item-header">
                <span className="o-feedback__history-date">
                  {formatDate(item.createdAt)}
                  {item.imageUrls && item.imageUrls.length > 0 && (
                    <span title={t('feedback.has_images', { count: item.imageUrls.length }) as string}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="o-feedback__history-img-icon">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </span>
                  )}
                </span>
                <StatusBadge status={item.status} />
              </div>
              <p className="o-feedback__history-content">
                {isExpanded
                  ? item.content
                  : item.content.length > 80
                    ? item.content.substring(0, 80) + '…'
                    : item.content}
              </p>

              {isExpanded && (
                <div className="o-feedback__history-detail">
                  {item.imageUrls && item.imageUrls.length > 0 && (
                    <div className="o-feedback__history-images">
                      {item.imageUrls.map((url, i) => (
                        <img key={i} src={url} alt={`feedback-img-${i}`} className="o-feedback__history-img" />
                      ))}
                    </div>
                  )}
                  {item.reply && (
                    <div className="o-feedback__history-reply">
                      <strong>{t('feedback.reply_label')}</strong>
                      <p>{item.reply}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Render helpers ─────────────────────
  const renderForm = () => (
    <form className="o-feedback__form" onSubmit={handleSubmit} noValidate>
      <div className="o-feedback__form-header">
        <div>
          <h2 className="o-feedback__title">{t('feedback.title')}</h2>
          <p className="o-feedback__subtitle">{t('feedback.subtitle')}</p>
        </div>
        {!isAnonymous && (
          <button
            type="button"
            className="o-feedback__history-btn"
            onClick={() => { setHistoryInitialized(false); setView('history'); }}
            title={t('feedback.history_title') as string}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{t('feedback.history_btn')}</span>
          </button>
        )}
      </div>

      {/* Contact: email or phone (anonymous only) */}
      {isAnonymous && (
        <div className={mapModifiers('o-feedback__field', errors.contact && 'error')}>

          <input
            type="text"
            className="o-feedback__input"
            placeholder={t('feedback.contact_placeholder') as string}
            value={contact}
            onChange={e => {
              setContact(e.target.value);
              if (errors.contact) setErrors(prev => ({ ...prev, contact: undefined }));
            }}
            autoComplete="email tel"
          />

          {errors.contact && <p className="o-feedback__error">{t(errors.contact)}</p>}
        </div>
      )}

      {/* Content */}
      <div className={mapModifiers('o-feedback__field', errors.content && 'error')}>
        <label className="o-feedback__label">
          {t('feedback.content_label')}
          <span className="o-feedback__required">*</span>
        </label>
        <textarea
          className="o-feedback__textarea"
          rows={5}
          placeholder={t('feedback.content_placeholder') as string}
          value={content}
          onChange={e => {
            setContent(e.target.value);
            if (errors.content) setErrors(prev => ({ ...prev, content: undefined }));
          }}
          maxLength={2000}
        />
        <span className="o-feedback__char-count">{content.length}/2000</span>
        {errors.content && <p className="o-feedback__error">{t(errors.content)}</p>}
      </div>

      {/* Images */}
      <div className="o-feedback__field">
        <label className="o-feedback__label">
          {t('feedback.image_label')}
          <span className="o-feedback__optional">
            ({t('feedback.max_images_hint', { max: maxImages, size: maxFileSizeMB })})
          </span>
        </label>
        <div className="o-feedback__images">
          {images.map((img, idx) => (
            <div key={idx} className="o-feedback__image-item">
              <img src={img.preview} alt={`preview-${idx}`} className="o-feedback__image-preview" />
              <button
                type="button"
                className="o-feedback__image-remove"
                onClick={() => removeImage(idx)}
                title={t('feedback.remove_image') as string}
              >
                ✕
              </button>
            </div>
          ))}
          {images.length < maxImages && (
            <label className="o-feedback__image-add">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleImageSelect}
              />
              <span className="o-feedback__image-add-icon">+</span>
              <span className="o-feedback__image-add-text">{t('feedback.add_image')}</span>
            </label>
          )}
        </div>
      </div>

      {/* CAPTCHA */}
      <div className={mapModifiers('o-feedback__field', errors.captcha && 'error')}>
        <label className="o-feedback__label">{t('feedback.captcha_label')}</label>
        <SimpleCaptcha onVerified={setCaptchaVerified} />
        {errors.captcha && <p className="o-feedback__error">{t(errors.captcha)}</p>}
      </div>

      {/* Device ID info */}
      <p className="o-feedback__device-info">
        <code></code>
      </p>

      {/* Actions */}
      <div className="o-feedback__actions">
        {mode === 'popup' && (
          <button type="button" className="o-feedback__btn o-feedback__btn--cancel" onClick={() => setIsOpen(false)}>
            {t('feedback.cancel')}
          </button>
        )}
        <button
          type="submit"
          className={mapModifiers('o-feedback__btn o-feedback__btn--submit', submitting && 'loading')}
          disabled={submitting}
        >
          {submitting ? t('feedback.sending') : t('feedback.send')}
        </button>
      </div>
    </form>
  );

  // ── Render: Content (form or history) ──
  const renderContent = () => (view === 'history' ? renderHistory() : renderForm());

  // ── Popup mode ─────────────────────────
  if (mode === 'popup') {
    const posStyle: React.CSSProperties = {};
    if (popupPosition.bottom !== undefined) posStyle.bottom = popupPosition.bottom;
    if (popupPosition.right !== undefined) posStyle.right = popupPosition.right;
    if (popupPosition.left !== undefined) posStyle.left = popupPosition.left;
    if (popupPosition.top !== undefined) posStyle.top = popupPosition.top;

    return (
      <>
        {/* Floating trigger button */}
        {!isOpen && (
          <button
            className="o-feedback__trigger"
            style={posStyle}
            onClick={() => {
              // Đóng chat Coze nếu đang mở
              const hideCozeChat = (window as Window & { __hideCozeChatBot?: () => void }).__hideCozeChatBot;
              if (typeof hideCozeChat === 'function') {
                hideCozeChat();
              }
              setIsOpen(true);
            }}
            title={t('feedback.title') as string}
            aria-label={t('feedback.title') as string}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="auto" height="auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-messages-square-icon lucide-messages-square"><path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /><path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" /></svg>
            {/* Chat / feedback icon SVG */}
          </button>
        )}

        {/* Popup panel */}
        {isOpen && (
          <>
            <div className="o-feedback__overlay" onClick={() => setIsOpen(false)} />
            <div className="o-feedback__popup" style={posStyle}>
              <button
                className="o-feedback__popup-close"
                onClick={() => setIsOpen(false)}
                title={t('feedback.close') as string}
              >
                ✕
              </button>
              {renderContent()}
            </div>
          </>
        )}
      </>
    );
  }

  // ── Full page mode ─────────────────────
  return (
    <div className="o-feedback o-feedback--page">
      <div className="o-feedback__container">{renderContent()}</div>
    </div>
  );
};

export default Feedback;
